// Public chain-verification page. Anyone can visit /verify with an HOA slug
// and a chain root hash (printed on every audit PDF) and independently
// confirm:
//   1. The hash chain has not been tampered with (each row's hash links to
//      the next row's prev_hash; sequence numbers are contiguous).
//   2. The user-supplied root hash appears at the head of the chain (or in
//      the latest signed checkpoint).
//
// All verification runs in the browser — we never trust the server's
// "verified ✓" claim. The browser fetches raw rows from /api/v1/public/audit
// and recomputes the link integrity itself.

import { useState } from 'react';

interface ChainRow {
  sequence: number;
  prevHash: string;
  hash: string;
  eventType: string;
  createdAt: number;
}

interface ChainResponse {
  tenant: { slug: string; name: string };
  chain: ChainRow[];
  nextAfterSequence: number | null;
}

interface CheckpointResponse {
  tenant: { slug: string; name: string };
  head: { sequence: number; hash: string } | null;
  latestSignedCheckpoint: {
    coversThroughSequence: number;
    coversThroughHash: string;
    signatureHex: string;
    signingKeyId: string;
    createdAt: number;
  } | null;
}

interface VerifyResult {
  status: 'pass' | 'fail';
  rowsChecked: number;
  brokenAtSequence?: number | undefined;
  brokenReason?: string | undefined;
  headHash?: string | undefined;
  rootHashFound?: boolean | undefined;
  rootHashSequence?: number | undefined;
  signedCheckpoint?: CheckpointResponse['latestSignedCheckpoint'] | undefined;
}

const API = import.meta.env.DEV ? '/api/v1' : `${import.meta.env.VITE_API_BASE ?? ''}/api/v1`;

export function Verify() {
  const [slug, setSlug] = useState('');
  const [rootHash, setRootHash] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<VerifyResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setResult(null);
    setRunning(true);
    try {
      // Pull all chain rows in pages.
      const allRows: ChainRow[] = [];
      let after = 0;
      let chainTenantName = '';
      while (true) {
        const res = await fetch(`${API}/public/audit/${encodeURIComponent(slug)}/chain?afterSequence=${after}&limit=2000`);
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error((j as { error?: { message?: string } }).error?.message ?? `HTTP ${res.status}`);
        }
        const body = (await res.json()) as ChainResponse;
        chainTenantName = body.tenant.name;
        allRows.push(...body.chain);
        if (body.nextAfterSequence == null) break;
        after = body.nextAfterSequence;
      }

      // Verify chain link integrity entirely client-side.
      const verify = verifyLinks(allRows);
      const headHash = allRows.length > 0 ? allRows[allRows.length - 1]!.hash : '0'.repeat(64);

      // Look up signed checkpoint.
      const cpRes = await fetch(`${API}/public/audit/${encodeURIComponent(slug)}/checkpoint`);
      const cp = (await cpRes.json()) as CheckpointResponse;

      const normalizedRoot = rootHash.trim().toLowerCase();
      const rootMatch = normalizedRoot
        ? allRows.find((r) => r.hash.toLowerCase() === normalizedRoot)
        : undefined;

      const out: VerifyResult = verify.ok
        ? {
            status: 'pass',
            rowsChecked: allRows.length,
            headHash,
            rootHashFound: normalizedRoot ? !!rootMatch : undefined,
            rootHashSequence: rootMatch?.sequence,
            signedCheckpoint: cp.latestSignedCheckpoint,
          }
        : {
            status: 'fail',
            rowsChecked: allRows.length,
            brokenAtSequence: verify.brokenAtSequence,
            brokenReason: verify.reason,
            headHash,
          };
      setResult(out);
      // Suppress unused
      void chainTenantName;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-3xl mx-auto p-6 sm:p-10">
        <div className="text-xl font-bold mb-1"><span className="text-brand">HOA-</span>Vote · Public verification</div>
        <p className="text-sm text-slate-600 mb-8">
          Independently verify any HOA's audit-chain integrity. This page fetches the raw chain from our public API and recomputes the link integrity in your browser — we never just take the server's word for it.
        </p>

        <form onSubmit={run} className="bg-white border border-slate-200 rounded p-5 mb-6">
          <label className="block mb-3">
            <span className="text-sm font-medium">HOA slug</span>
            <input required value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="e.g. magnolia-grove"
              className="mt-1 w-full border border-slate-300 rounded px-3 py-2" />
          </label>
          <label className="block mb-4">
            <span className="text-sm font-medium">Chain root hash <span className="text-slate-400 font-normal">(optional — from your audit PDF receipt)</span></span>
            <input value={rootHash} onChange={(e) => setRootHash(e.target.value)} placeholder="64 hex characters"
              className="mt-1 w-full border border-slate-300 rounded px-3 py-2 font-mono text-xs" />
          </label>
          {error && <div className="text-red-700 text-sm mb-3">{error}</div>}
          <button type="submit" disabled={running || !slug}
            className="bg-brand text-white px-5 py-2 rounded font-medium disabled:opacity-50">
            {running ? 'Verifying...' : 'Verify chain'}
          </button>
        </form>

        {result && (
          <div className={`border rounded p-5 ${result.status === 'pass' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 grid place-items-center rounded-full font-bold text-lg ${result.status === 'pass' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
                {result.status === 'pass' ? '✓' : '✗'}
              </div>
              <div>
                <div className="font-semibold">
                  {result.status === 'pass' ? 'Chain integrity verified' : 'Chain integrity FAILED'}
                </div>
                <div className="text-xs text-slate-600">{result.rowsChecked} rows checked</div>
              </div>
            </div>
            {result.status === 'fail' && (
              <p className="mt-3 text-sm text-red-700">
                Failure at sequence <code>{result.brokenAtSequence}</code>: {result.brokenReason}
              </p>
            )}
            {result.status === 'pass' && (
              <div className="mt-3 text-sm space-y-2">
                <div>
                  <div className="text-xs text-slate-500">Current chain head hash</div>
                  <code className="font-mono text-xs break-all">{result.headHash}</code>
                </div>
                {rootHash && (
                  <div>
                    <div className="text-xs text-slate-500">Your supplied root hash</div>
                    {result.rootHashFound ? (
                      <div className="text-green-800">Found at sequence <strong>{result.rootHashSequence}</strong>. Receipt is genuine and the chain has not been tampered with through that point.</div>
                    ) : (
                      <div className="text-red-800">Not found in this HOA's chain. Either the hash was mistyped or the receipt is fraudulent.</div>
                    )}
                  </div>
                )}
                {result.signedCheckpoint && (
                  <div>
                    <div className="text-xs text-slate-500">Latest signed checkpoint</div>
                    <div>Sequence <strong>{result.signedCheckpoint.coversThroughSequence}</strong> · key <code>{result.signedCheckpoint.signingKeyId}</code> · {new Date(result.signedCheckpoint.createdAt).toLocaleString()}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <details className="mt-8 text-sm text-slate-600">
          <summary className="cursor-pointer font-medium">How verification works</summary>
          <div className="mt-2 space-y-2">
            <p>Each event in the audit log is hashed with sha256 of the previous row's hash plus the event type, timestamp, and a canonical-JSON of the payload. The result is the row's <code>hash</code>. The next row's <code>prev_hash</code> is set to that value, forming a chain.</p>
            <p>Tampering with any row breaks every subsequent hash. Inserting, removing, or reordering rows breaks the link integrity check.</p>
            <p>This page reads raw rows directly from the public API and recomputes the chain integrity locally. Payloads are not exposed publicly (they contain resident PII) but anyone who has an audit PDF receipt for a specific row can verify that row's hash appears in the chain.</p>
          </div>
        </details>
      </div>
    </div>
  );
}

function verifyLinks(rows: ChainRow[]): { ok: true } | { ok: false; brokenAtSequence: number; reason: string } {
  let prevHash = '0'.repeat(64);
  let prevSeq = 0;
  for (const r of rows) {
    if (r.sequence !== prevSeq + 1) {
      return { ok: false, brokenAtSequence: r.sequence, reason: `Sequence gap (expected ${prevSeq + 1})` };
    }
    if (r.prevHash !== prevHash) {
      return { ok: false, brokenAtSequence: r.sequence, reason: `prev_hash does not match previous row's hash` };
    }
    prevHash = r.hash;
    prevSeq = r.sequence;
  }
  return { ok: true };
}
